/**
 * Zeus hero cinema — GSAP ScrollTrigger scrub (frame expand + panel reveal).
 * Fallback vanilla si GSAP indisponible.
 */
(function () {
  var INTRO_MS = 900;
  var heroRoots = [];
  var PANEL_ITEM_SELECTOR =
    ".zeus-hero-video__section-header .zeus-badge, .zeus-hero-video__title-wrap, .zeus-hero-video__subtitle, .zeus-hero-video__ctas > *";

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function isMobile() {
    return window.matchMedia("(max-width: 699px)").matches;
  }

  function ensureHeaderVisible(root) {
    root.classList.add("is-header-visible");
  }

  // Track a global listener so it can be removed on section:load re-render.
  function addTracked(root, target, type, handler) {
    target.addEventListener(type, handler, { passive: true });
    if (!root._zeusHeroCinemaListeners) root._zeusHeroCinemaListeners = [];
    root._zeusHeroCinemaListeners.push({ target: target, type: type, handler: handler });
  }

  // Kill ScrollTrigger instances scoped to this root (mirrors zeus-featured-boards killCardTriggers).
  function killHeroTriggers(root) {
    var ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger) return;
    ScrollTrigger.getAll()
      .filter(function (t) {
        return t.vars && t.vars.trigger && root.contains(t.vars.trigger);
      })
      .forEach(function (t) {
        t.kill();
      });
  }

  // Remove all tracked listeners + kill triggers before a re-init.
  function cleanupHero(root) {
    if (root._zeusHeroCinemaListeners) {
      root._zeusHeroCinemaListeners.forEach(function (l) {
        l.target.removeEventListener(l.type, l.handler);
      });
      root._zeusHeroCinemaListeners = [];
    }
    killHeroTriggers(root);
    root.dataset.zeusHeroCinemaInit = "false";
    var index = heroRoots.indexOf(root);
    if (index !== -1) heroRoots.splice(index, 1);
  }

  function trackHero(root) {
    if (heroRoots.indexOf(root) === -1) heroRoots.push(root);
  }

  function runIntro(root) {
    if (prefersReducedMotion()) {
      root.classList.remove("is-intro");
      return;
    }

    window.setTimeout(function () {
      root.classList.remove("is-intro");
    }, INTRO_MS);
  }

  function setProgress(root, value) {
    var p = Math.min(1, Math.max(0, value));
    root.style.setProperty("--cinema-p", String(p));
    root.classList.toggle("is-expanded", p >= 0.98);
    ensureHeaderVisible(root);
  }

  function setStatic(root) {
    root.classList.add("is-static", "is-expanded");
    root.classList.remove("is-intro");
    setProgress(root, 1);
  }

  function initReducedMotion(root) {
    setStatic(root);
  }

  function initMobile(root) {
    root.classList.add("is-static", "is-header-visible");
    root.classList.remove("is-intro");
    setProgress(root, 1);
  }

  function skipToEnd(root, scroller, smooth) {
    var top = scroller.offsetTop + scroller.offsetHeight - window.innerHeight;
    var target = Math.max(0, top);
    if (smooth && window.gsap) {
      var pos = { y: window.scrollY };
      window.gsap.to(pos, {
        y: target,
        duration: 0.85,
        ease: "power2.inOut",
        onUpdate: function () {
          window.scrollTo(0, pos.y);
        },
      });
    } else {
      window.scrollTo({ top: target, behavior: smooth ? "smooth" : "auto" });
    }
    setProgress(root, 1);
  }

  function initVanilla(root) {
    var scroller = root.querySelector("[data-zeus-hero-cinema-scroller]");
    if (!scroller) return;

    if (prefersReducedMotion()) {
      initReducedMotion(root);
      return;
    }

    if (isMobile()) {
      initMobile(root);
      return;
    }

    if (root.dataset.zeusHeroCinemaInit === "true") return;
    root.dataset.zeusHeroCinemaInit = "true";

    var ticking = false;
    function updateFromScroll() {
      var scrollable = scroller.offsetHeight - window.innerHeight;
      if (scrollable <= 0) {
        setProgress(root, 1);
        return;
      }
      var rect = scroller.getBoundingClientRect();
      setProgress(root, -rect.top / scrollable);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        updateFromScroll();
        ticking = false;
      });
    }

    addTracked(root, window, "scroll", onScroll);
    addTracked(root, window, "resize", onScroll);
    bindStackHandoffVanilla(root, scroller);
    runIntro(root);
    updateFromScroll();
  }

  function initGsap(root) {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    var scroller = root.querySelector("[data-zeus-hero-cinema-scroller]");
    var panel = root.querySelector(".zeus-hero-video__cinema-panel");
    var hint = root.querySelector(".zeus-hero-video__scroll-hint--infobar");
    if (!scroller) return;

    root.classList.add("is-gsap");

    if (prefersReducedMotion()) {
      initReducedMotion(root);
      return;
    }

    if (isMobile()) {
      initMobile(root);
      return;
    }

    if (root.dataset.zeusHeroCinemaInit === "true") return;
    root.dataset.zeusHeroCinemaInit = "true";

    var panelItems = panel ? panel.querySelectorAll(PANEL_ITEM_SELECTOR) : [];

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: scroller,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.7,
        invalidateOnRefresh: true,
        onUpdate: function (self) {
          setProgress(root, self.progress);
        },
      },
    });

    if (hint) {
      tl.fromTo(hint, { opacity: 1 }, { opacity: 0, ease: "power1.in", duration: 0.2 }, 0.08);
    }

    bindStackHandoff(root, scroller);
    runIntro(root);
  }

  function bindStackHandoff(root, scroller) {
    if (prefersReducedMotion()) return;

    var scrollBtn = root.querySelector("[data-zeus-hero-scroll-target]");
    var stackSel = scrollBtn ? scrollBtn.getAttribute("data-zeus-hero-scroll-target") : "[data-zeus-stacking-cards]";
    var stackSection = document.querySelector(stackSel);
    if (!stackSection || !scroller || !window.gsap || !window.ScrollTrigger) {
      bindStackHandoffVanilla(root, scroller);
      return;
    }

    window.ScrollTrigger.create({
      trigger: scroller,
      start: "bottom 92%",
      end: "bottom top",
      scrub: 0.55,
      invalidateOnRefresh: true,
      onUpdate: function (self) {
        stackSection.style.setProperty("--hero-handoff-p", String(self.progress));
        stackSection.classList.toggle("is-hero-handoff", self.progress > 0.02 && self.progress < 0.98);
        stackSection.classList.toggle("is-hero-handoff-done", self.progress >= 0.98);
      },
    });
  }

  function bindStackHandoffVanilla(root, scroller) {
    if (prefersReducedMotion()) return;

    var scrollBtn = root.querySelector("[data-zeus-hero-scroll-target]");
    var stackSel = scrollBtn ? scrollBtn.getAttribute("data-zeus-hero-scroll-target") : "[data-zeus-stacking-cards]";
    var stackSection = document.querySelector(stackSel);
    if (!stackSection || !scroller) {
      return;
    }

    var ticking = false;

    function updateHandoff() {
      var rect = scroller.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      var progress = Math.min(1, Math.max(0, (vh - rect.bottom) / (vh * 0.55)));
      stackSection.style.setProperty("--hero-handoff-p", String(progress));
      stackSection.classList.toggle("is-hero-handoff", progress > 0.02 && progress < 0.98);
      stackSection.classList.toggle("is-hero-handoff-done", progress >= 0.98);
    }

    function onScroll() {
      if (ticking) {
        return;
      }
      ticking = true;
      requestAnimationFrame(function () {
        updateHandoff();
        ticking = false;
      });
    }

    addTracked(root, window, "scroll", onScroll);
    addTracked(root, window, "resize", onScroll);
    updateHandoff();
  }

  // Dispatch a single root through the correct init path. Assumes GSAP
  // availability has already been resolved by the caller.
  function bootRoot(root, hasGsap) {
    trackHero(root);
    ensureHeaderVisible(root);
    if (prefersReducedMotion()) {
      initReducedMotion(root);
    } else if (isMobile()) {
      initMobile(root);
    } else if (hasGsap) {
      initGsap(root);
    } else {
      initVanilla(root);
    }
  }

  function boot() {
    var roots = document.querySelectorAll("[data-zeus-hero-cinema]");
    if (!roots.length) return;

    roots.forEach(trackHero);
    roots.forEach(ensureHeaderVisible);

    if (prefersReducedMotion() || isMobile()) {
      roots.forEach(function (root) {
        if (prefersReducedMotion()) {
          initReducedMotion(root);
        } else {
          initMobile(root);
        }
      });
      return;
    }

    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      roots.forEach(initGsap);
    } else {
      roots.forEach(initVanilla);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Late GSAP (idle-loaded): upgrade vanilla desktop cinema when scripts arrive
  window.addEventListener("zeus:gsap-ready", function () {
    if (prefersReducedMotion() || isMobile()) return;
    if (!window.gsap || !window.ScrollTrigger) return;
    window.gsap.registerPlugin(window.ScrollTrigger);
    document.querySelectorAll("[data-zeus-hero-cinema]").forEach(function (root) {
      if (root.classList.contains("is-gsap")) return;
      cleanupHero(root);
      bootRoot(root, true);
    });
  });

  // Theme editor re-render: tear down stale listeners/triggers for the reloaded
  // section, then re-init that root (mirrors zeus-featured-boards.js).
  document.addEventListener("shopify:section:load", function (event) {
    heroRoots.slice().forEach(function (existingRoot) {
      if (!document.contains(existingRoot)) cleanupHero(existingRoot);
    });

    var root = event.target.querySelector("[data-zeus-hero-cinema]");
    if (!root) return;

    var hasGsap = !!(window.gsap && window.ScrollTrigger);
    if (hasGsap) window.gsap.registerPlugin(window.ScrollTrigger);
    bootRoot(root, hasGsap);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    heroRoots.slice().forEach(function (root) {
      if (event.target.contains(root) || !document.contains(root)) cleanupHero(root);
    });
  });
})();
